import { ImageUploadPlaceholder } from "@/components/customer/events/ImageUploadPlaceholder";
import { useTranslation } from "@/hooks/useTranslation";
import { deleteGiftImage, uploadGiftImage } from "@/services/api/eventSettings";

interface GiftImageFieldProps {
  eventUuid: string;
  /** Public uuid of the gift/fund; absent while a new gift is not saved yet. */
  giftUuid?: string | null;
  label: string;
  /** Current image URL ("" when none). */
  value: string;
  /** Called after the server stored or removed the image. */
  onChange: (url: string) => void;
  disabled?: boolean;
}

/**
 * JUB-39: a gift's (or cash fund's) cover image — upload, preview, replace,
 * remove. Every action goes straight to the gift image endpoint (JUB-144), so
 * the image is saved even if the surrounding form is cancelled; the form only
 * echoes the URL back.
 */
export function GiftImageField({ eventUuid, giftUuid, label, value, onChange, disabled }: GiftImageFieldProps) {
  const { t } = useTranslation();

  return (
    <div data-testid="gift-image-field">
      <ImageUploadPlaceholder
        label={label}
        currentImageUrl={value || null}
        disabled={disabled}
        unavailableHint={t("events.form.giftImageAfterSave")}
        onUpload={giftUuid ? (file) => uploadGiftImage(eventUuid, giftUuid, file) : undefined}
        onChange={async (url) => {
          if (url === null) {
            if (giftUuid && value) {
              await deleteGiftImage(eventUuid, giftUuid);
            }
            onChange("");
            return;
          }
          onChange(url);
        }}
      />
      {!giftUuid && (
        <p className="mt-1 text-xs text-muted-foreground">{t("events.form.giftImageAfterSave")}</p>
      )}
    </div>
  );
}
